/**
 * "Resulting figures" (f10.4, design D6): what the cycle's master list will
 * be if the batch is committed as decided. Recomputed by the server on every
 * read, so it moves after each decision without any arithmetic here.
 */

import type { ReactNode } from 'react';

import type { UsEngagementsFigures } from '@/api/client';

function Stat({ label, value, foot }: { label: string; value: number; foot: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value.toLocaleString()}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: readonly { label: string; count: number }[] }) {
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            <th>{title}</th>
            <th className="num">Elements</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={2} className="hint">
                No element is in the master list yet.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td className="num">{row.count.toLocaleString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function FiguresCard({ figures, footer }: { figures: UsEngagementsFigures; footer?: ReactNode }) {
  const { accounts, engagements, elements, master_list } = figures;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Resulting figures</h3>
        <span className="hint">Updated after every decision</span>
      </div>
      <div className="card-body stack">
        <div className="stat-grid">
          <Stat
            label="Accounts"
            value={accounts.total}
            foot={
              <>
                {accounts.matched} matched &middot; {accounts.confirm} new &middot; {accounts.map} mapped
                &middot; {accounts.exclude} excluded &middot; {accounts.undecided} undecided
                {accounts.sap_ids_to_record > 0 && <> &middot; {accounts.sap_ids_to_record} SAP IDs to record</>}
              </>
            }
          />
          <Stat
            label="Engagements"
            value={engagements.total}
            foot={
              <>
                {engagements.matched} matched &middot; {engagements.confirm} new &middot;{' '}
                {engagements.exclude + engagements.excluded_by_parent} excluded &middot; {engagements.undecided}{' '}
                undecided
              </>
            }
          />
          <Stat
            label="Elements"
            value={elements.total}
            foot={
              <>
                {elements.matched} matched &middot; {elements.confirm} new &middot;{' '}
                {elements.exclude + elements.excluded_by_parent} excluded &middot; {elements.undecided} undecided
              </>
            }
          />
          <Stat label="Master list" value={master_list.element_count} foot="Elements in this cycle's master list" />
        </div>
        <div className="grid grid-2">
          <Breakdown title="Business Unit" rows={master_list.by_business_unit} />
          <Breakdown title="Delivery stage" rows={master_list.by_stage} />
        </div>
      </div>
      {footer}
    </div>
  );
}
